import styled from 'styled-components';
import type { FleetGroupDetail, FleetGroupModel } from '@shared/api';
import { pluralizeRu } from '@shared/lib/formatFile';
import {
  BlockHeading,
  BlockNote,
  BlockSection,
  FleetStatusChip,
  Notice,
  SpecGrid,
  fleetLeading,
  fleetType,
  formatCoef,
  formatCount,
  formatIsoDate,
  formatMoney,
  formatShare,
  groupUnitLabel,
  rateUnit,
  typo,
} from '@entities/fleet';

const Count = styled.span`
  font-size: ${fleetType.small};
  font-weight: 400;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-variant-numeric: tabular-nums;
`;

const PositionList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
  margin: 0;
  padding: 0;
  list-style: none;

  & > li {
    min-width: 0;
    padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(3)}`};
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: ${({ theme }) => theme.radius.sm};
    background: ${({ theme }) => theme.colors.surface};
  }

  & .name {
    display: block;
    font-size: ${fleetType.body};
    color: ${({ theme }) => theme.colors.text.primary};
    overflow-wrap: anywhere;
  }

  & .key {
    display: block;
    font-family: ${({ theme }) => theme.font.mono};
    font-size: ${fleetType.micro};
    color: ${({ theme }) => theme.colors.text.secondary};
    overflow-wrap: anywhere;
  }
`;

/** Модели группы: таблица, в узкой панели — строки-карточки. */
const Models = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.normal};

  caption {
    padding-bottom: ${({ theme }) => theme.spacing(1.5)};
    text-align: left;
    font-size: ${fleetType.small};
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  th,
  td {
    padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(2.5)}`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    vertical-align: top;
  }

  thead th {
    font-size: ${fleetType.micro};
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    text-align: left;
    color: ${({ theme }) => theme.colors.text.secondary};
    border-bottom-color: ${({ theme }) => theme.colors.borderStrong};
    vertical-align: bottom;
  }

  .num {
    text-align: right;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }

  td.none {
    font-size: ${fleetType.small};
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  .narrow-only {
    display: none;
  }

  @container drawer (max-width: 520px) {
    &,
    & > tbody,
    & tr,
    & td {
      display: block;
    }

    & > thead,
    .wide-only {
      display: none;
    }

    .narrow-only {
      display: block;
    }

    tr {
      padding: ${({ theme }) => `${theme.spacing(2.5)} 0`};
      border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    }

    tr > td {
      padding: 0;
      border: none;
    }
  }
`;

const ModelName = styled.span`
  display: block;
  font-size: ${fleetType.base};
  color: ${({ theme }) => theme.colors.text.primary};
  overflow-wrap: anywhere;
`;

const Sub = styled.span`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2)}`};
  margin-top: ${({ theme }) => theme.spacing(1)};
  font-size: ${fleetType.small};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const ManualMark = styled.span`
  padding: 0 ${({ theme }) => theme.spacing(1.5)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.xs};
  font-size: ${fleetType.micro};
  line-height: 16px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Comment = styled.p`
  margin: ${({ theme }) => `${theme.spacing(4)} 0 0`};
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.normal};
  white-space: pre-wrap;

  & > .k {
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

function ModelRow({ model }: { model: FleetGroupModel }) {
  const prc = model.prc_price !== null;
  return (
    <tr>
      <td>
        <ModelName>{model.name}</ModelName>
        <Sub>
          {model.category_name && <span>{model.category_name}</span>}
          {model.status !== 'active' && <FleetStatusChip status={model.status} />}
          {!model.auto && (
            <ManualMark title="Модель поставил экономист, а не подбор по должности механизатора">
              добавлена вручную
            </ManualMark>
          )}
        </Sub>
        <Sub className="narrow-only">
          {formatCount(model.units_count)} ед. ·{' '}
          {prc
            ? `ПРЦ ${formatMoney(model.prc_price)} ${rateUnit('machine_hour')} без НДС, с ${formatIsoDate(model.prc_valid_from)}`
            : 'нет ПРЦ на сегодня'}
        </Sub>
      </td>
      <td className="wide-only num">{formatCount(model.units_count)}</td>
      {prc ? (
        <>
          <td className="wide-only num">{formatMoney(model.prc_cost)}</td>
          <td className="wide-only num">{formatMoney(model.prc_price)}</td>
          <td className="wide-only num">{formatIsoDate(model.prc_valid_from)}</td>
        </>
      ) : (
        <td className="wide-only none" colSpan={3}>
          нет ПРЦ на сегодня
        </td>
      )}
    </tr>
  );
}

/**
 * Сколько моделей группы с ПРЦ на сегодня — из них считается подсказка ставки
 * на сегодняшнюю дату. Цена с будущей датой войдёт в подсказку с этой даты.
 */
function coverageText(models: readonly FleetGroupModel[]): string {
  const live = models.filter((m) => m.status !== 'archived');
  const priced = live.filter((m) => m.prc_price !== null).length;
  if (live.length === 0) return '';
  if (priced === live.length) {
    return 'ПРЦ на сегодня есть у всех моделей — подсказка средней ставки считается по всему составу.';
  }
  return typo(
    `ПРЦ на сегодня есть у ${priced} из ${live.length} ${pluralizeRu(live.length, ['модели', 'моделей', 'моделей'])}. Модели без действующей ПРЦ в подсказку средней ставки на эту дату не входят.`,
  );
}

/** Карточка группы в режиме просмотра: учёт, должности Хронума, состав с ПРЦ. */
export function GroupView({ detail, idPrefix }: { detail: FleetGroupDetail; idPrefix: string }) {
  const { group, models } = detail;
  const day = group.unit === 'day';
  const coverage = coverageText(models);
  return (
    <>
      <SpecGrid aria-label="Учёт группы">
        <div>
          <dt>Учёт</dt>
          <dd>{groupUnitLabel(group.unit)}</dd>
        </div>
        <div>
          <dt>Маш.-ч на час механизатора</dt>
          <dd className={day ? 'empty' : undefined}>
            {day ? 'не применяется' : formatCoef(group.hours_coef)}
          </dd>
        </div>
        <div>
          <dt>Доля ставки за простой</dt>
          <dd>{formatShare(group.downtime_share)}</dd>
        </div>
        <div>
          <dt>Моделей · единиц</dt>
          <dd>
            {formatCount(group.models_count)} · {formatCount(group.units_count)} ед.
          </dd>
        </div>
      </SpecGrid>

      {group.archived && (
        <Notice $tone="info">
          <span className="text">
            {typo(
              'Группа в архиве: её модели и должности Хронума освобождены для других групп, ставки остались в истории. Вернуть группу можно — состав нужно будет собрать заново.',
            )}
          </span>
        </Notice>
      )}

      <BlockSection aria-labelledby={`${idPrefix}-positions-h`}>
        <BlockHeading id={`${idPrefix}-positions-h`}>
          Должности Хронума <Count>{group.positions.length}</Count>
        </BlockHeading>
        {group.positions.length > 0 ? (
          <>
            <BlockNote>{typo('Часы этих должностей в Хронуме — маш.-часы группы.')}</BlockNote>
            <PositionList>
              {group.positions.map((p) => (
                <li key={p.key}>
                  <span className="name">{p.name}</span>
                  <span className="key">{p.key}</span>
                </li>
              ))}
            </PositionList>
          </>
        ) : (
          <BlockNote>
            {typo(
              group.archived
                ? 'Должностей нет: при архивации группа их освободила.'
                : day
                  ? 'Группа учитывается в сутках на объекте — должности Хронума ей не нужны.'
                  : 'Должности не заданы: часы механизаторов из Хронума в эту группу не попадут. Добавьте должность в правке группы.',
            )}
          </BlockNote>
        )}
      </BlockSection>

      <BlockSection aria-labelledby={`${idPrefix}-models-h`}>
        <BlockHeading id={`${idPrefix}-models-h`}>
          Модели группы{' '}
          <Count>
            {formatCount(models.length)} · {formatCount(group.units_count)} ед.
          </Count>
        </BlockHeading>
        {models.length === 0 ? (
          <BlockNote>
            {typo(
              group.archived
                ? 'Моделей нет: при архивации группа их освободила.'
                : 'В группе нет моделей. Добавьте их в правке группы или подберите по должностям механизаторов.',
            )}
          </BlockNote>
        ) : (
          <>
            {coverage && <BlockNote>{coverage}</BlockNote>}
            <Models>
              <caption>ПРЦ на сегодня, {rateUnit('machine_hour')} без НДС</caption>
              <thead>
                <tr>
                  <th scope="col">Модель</th>
                  <th scope="col" className="num">
                    Ед.
                  </th>
                  <th scope="col" className="num">
                    Себестоимость
                  </th>
                  <th scope="col" className="num">
                    ПРЦ без НДС
                  </th>
                  <th scope="col" className="num">
                    Действует с
                  </th>
                </tr>
              </thead>
              <tbody>
                {models.map((m) => (
                  <ModelRow key={m.model_id} model={m} />
                ))}
              </tbody>
            </Models>
          </>
        )}
      </BlockSection>

      {group.comment && (
        <Comment>
          <span className="k">Комментарий: </span>
          {group.comment}
        </Comment>
      )}
    </>
  );
}
